import React, { useState, useCallback, useEffect } from "react";
import { RouteComponentProps, navigate } from "@reach/router";
import { FiPlus } from "react-icons/fi";
import { trackMySuppliersPageViewed } from "../utils/analytics";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import { Pagination } from "newui/components/Pagination/Pagination";
import { usePagination } from "newui/components/Pagination/usePagination";
import { SkeletonBase } from "newui/components/Skeleton/Skeleton";
import { useMySuppliers } from "../hooks/useMySuppliers";
import { useSupplierPerformancePermissions } from "../hooks/useSupplierPerformancePermissions";
import { MySuppliersTable } from "../components/MySuppliersList/MySuppliersTable";
import { MySuppliersListToolbar } from "../components/MySuppliersList/MySuppliersListToolbar";
import { MySuppliersEmptyState } from "../components/MySuppliersList/MySuppliersEmptyState";
import type {
	AwardedSupplier,
	MySuppliersFilters,
} from "../types/supplier-performance.types";

const DEFAULT_FILTERS: MySuppliersFilters = {
	search: "",
	supplierType: [],
	performanceBand: [],
	isActive: null,
	sortBy: "name",
	sortOrder: "asc",
	page: 1,
	perPage: 20,
};

interface MySuppliersPageProps extends RouteComponentProps {
	companyId?: string;
}

export const MySuppliersPage: React.FC<MySuppliersPageProps> = () => {
	const [filters, setFilters] = useState<MySuppliersFilters>(DEFAULT_FILTERS);
	const { clientPaths } = useNavigationPaths();
	const { canCreate } = useSupplierPerformancePermissions();

	const { data, isLoading, isError } = useMySuppliers(filters);

	useEffect(() => {
		trackMySuppliersPageViewed();
	}, []);

	const pagination = usePagination({
		totalItems: data?.meta?.total ?? 0,
		initialPage: filters.page,
		initialRowsPerPage: filters.perPage,
		onPageChange: (page) => {
			setFilters((prev) => ({ ...prev, page }));
		},
		onRowsPerPageChange: (perPage) => {
			setFilters((prev) => ({ ...prev, perPage, page: 1 }));
		},
	});

	const handleFiltersChange = useCallback(
		(updates: Partial<MySuppliersFilters>) => {
			setFilters((prev) => ({ ...prev, ...updates }));
		},
		[]
	);

	const handleSupplierClick = useCallback(
		(supplier: AwardedSupplier) => {
			if (clientPaths) {
				navigate(
					clientPaths.viewSupplierDetail(
						supplier.supplier_company_id
					)
				);
			}
		},
		[clientPaths]
	);

	const handleRateClick = useCallback(
		(supplier: AwardedSupplier) => {
			if (clientPaths) {
				navigate(clientPaths.rateSupplier(supplier.supplier_company_id));
			}
		},
		[clientPaths]
	);

	const suppliers = data?.data ?? [];
	const totalItems = data?.meta?.total ?? 0;
	const hasSuppliers = totalItems > 0 || filters.search || filters.supplierType.length > 0 || filters.performanceBand.length > 0;

	return (
		<div className="p-6" data-testid="my-suppliers-page">
			<div className="mb-6 flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-semibold text-gray-900">
						Supplier Hub
					</h1>
					<p className="mt-1 text-sm text-gray-500">
						View and manage your suppliers and their
						performance ratings.
					</p>
				</div>
				{canCreate && (
					<button
						type="button"
						onClick={() => {
							if (clientPaths) {
								navigate(clientPaths.mySuppliers.rate);
							}
						}}
						className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
						data-testid="rate-supplier-cta"
					>
						<FiPlus className="h-4 w-4" />
						Rate Supplier
					</button>
				)}
			</div>

			{isLoading && (
				<div className="space-y-4" data-testid="my-suppliers-loading">
					<SkeletonBase width="100%" height="56px" />
					<SkeletonBase width="100%" height="400px" />
				</div>
			)}

			{isError && (
				<div
					className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
					data-testid="my-suppliers-error"
				>
					Unable to load suppliers. Please try again later.
				</div>
			)}

			{!isLoading && !isError && !hasSuppliers && (
				<MySuppliersEmptyState variant="no-suppliers" />
			)}

			{!isLoading && !isError && hasSuppliers && (
				<>
					<MySuppliersListToolbar
						filters={filters}
						onFiltersChange={handleFiltersChange}
					/>

					{suppliers.length === 0 ? (
						<div className="mt-4">
							<MySuppliersEmptyState variant="no-ratings" />
						</div>
					) : (
						<>
							<div className="mt-4">
								<MySuppliersTable
									suppliers={suppliers}
									onSupplierClick={handleSupplierClick}
									onRateClick={handleRateClick}
									canCreate={canCreate}
								/>
							</div>

							<Pagination
								currentPage={pagination.currentPage}
								rowsPerPage={pagination.rowsPerPage}
								totalItems={totalItems}
								onPageChange={pagination.setCurrentPage}
								onRowsPerPageChange={pagination.setRowsPerPage}
								testId="my-suppliers-pagination"
							/>
						</>
					)}
				</>
			)}
		</div>
	);
};
